import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { EyeIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { extintorService, Extintor } from '../services/extintorService';
import DataTable, { FilterConfig, ServerPageBar } from '../components/DataTable';
import Notification from '../components/Notification';
import ViewToggle from '../components/ViewToggle';
import { useClienteSucursalFilter } from '../hooks/useClienteSucursalFilter';
import { useAbortableLoad } from '../hooks/useAbortableLoad';
import { useListUrlState } from '../hooks/useListUrlState';
import { isAbortError, LIST_PAGE_SIZE, hasClienteFilter, toListQuery } from '../utils/listSearch';
import { useCenter } from '../contexts/CenterContext';

const EXTINTOR_ESTADOS = [
  { value: 'vencidosCarga', label: 'Vencidos carga' },
  { value: 'vencidosPh', label: 'Vencidos PH' },
  { value: 'porVencer', label: 'Por vencer (30 días)' },
  { value: 'reserva', label: 'En reserva' },
] as const;

const GATE_EMPTY = 'Elegí un cliente o un estado en Filtros para ver los registros.';
const GATE_SEARCH_HINT =
  'Filtrá por cliente/estado (y opcionalmente sucursal). Luego buscá con al menos 3 letras (sucursal, código, serie, ubicación).';

function hasEstadoFilter(filters: Record<string, any>): boolean {
  return typeof filters.estado === 'string' && filters.estado.trim() !== '';
}

function canLoadExtintores(filters: Record<string, any>): boolean {
  return hasClienteFilter(filters) || hasEstadoFilter(filters);
}

const ExtintoresPage: React.FC = () => {
  const [extintores, setExtintores] = useState<Extintor[]>([]);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('table');
  const [notification, setNotification] = useState<{
    type: 'success' | 'error' | 'info';
    message: string;
  } | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState<number | undefined>(undefined);
  const [currentFilters, setCurrentFilters] = useState<Record<string, any>>({});
  const { clienteOptions, sucursalOptions, loadSucursalesForCliente } = useClienteSucursalFilter();
  const nextSignal = useAbortableLoad();
  const navigate = useNavigate();
  const { filtersFromUrl, pageFromUrl, writeUrlState, searchParams } = useListUrlState('estado');
  const { currentCenter } = useCenter();
  const centerId = currentCenter?.id;

  const loadExtintores = useCallback(async (filters: Record<string, any> = {}, pageNum = 1) => {
    if (!canLoadExtintores(filters)) {
      setExtintores([]);
      setHasMore(false);
      setTotalCount(undefined);
      setPage(1);
      setLoading(false);
      return;
    }
    const signal = nextSignal();
    try {
      setLoading(true);
      const estado =
        typeof filters.estado === 'string' && filters.estado.trim()
          ? filters.estado.trim()
          : undefined;
      const result = await extintorService.list(
        {
          ...toListQuery(filters),
          estado,
          page: pageNum,
          pageSize: LIST_PAGE_SIZE,
        },
        signal,
      );
      setExtintores(result.data);
      setHasMore(result.hasMore);
      setTotalCount(result.totalCount);
      setPage(result.page);
    } catch (error) {
      if (isAbortError(error)) return;
      console.error('Error al cargar extintores:', error);
      setNotification({
        type: 'error',
        message: 'Error al cargar los extintores',
      });
    } finally {
      if (!signal.aborted) setLoading(false);
    }
  }, [nextSignal]);

  useEffect(() => {
    if (!centerId) return;
    const fromUrl = { ...filtersFromUrl };
    if (fromUrl.estado && !EXTINTOR_ESTADOS.some((e) => e.value === fromUrl.estado)) {
      delete fromUrl.estado;
    }
    setCurrentFilters(fromUrl);
    void loadSucursalesForCliente(fromUrl.cliente);
    if (canLoadExtintores(fromUrl)) {
      void loadExtintores(fromUrl, pageFromUrl);
    } else {
      setExtintores([]);
      setHasMore(false);
      setTotalCount(undefined);
      setPage(1);
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reaccionar a URL/centro
  }, [centerId, searchParams]);

  const handleFilterChange = async (filters: Record<string, any>) => {
    setCurrentFilters(filters);
    writeUrlState(filters, 1);
  };

  const handlePageChange = (next: number) => {
    writeUrlState(currentFilters, next);
  };

  const handleAdd = () => {
    navigate('/extintores/nuevo');
  };

  const handleEdit = (extintor: Extintor) => {
    navigate(`/extintores/${extintor.id}/editar`);
  };

  const handleDelete = async (extintor: Extintor) => {
    if (
      window.confirm(
        '¿Estás seguro de que quieres desactivar este extintor? Esta acción no se puede deshacer.',
      )
    ) {
      try {
        const result = await extintorService.delete(extintor.id);
        // Recargar la lista para mostrar el estado actualizado
        await loadExtintores(currentFilters, page);
        setNotification({
          type: 'success',
          message: result.message || 'Extintor desactivado correctamente',
        });
      } catch (error) {
        console.error('Error al desactivar extintor:', error);
        setNotification({
          type: 'error',
          message: 'Error al desactivar el extintor',
        });
      }
    }
  };

  const handleView = (extintor: Extintor) => {
    navigate(`/extintores/${extintor.id}`);
  };

  const columns = [
    {
      key: 'sucursal',
      label: 'Sucursal',
      sortable: true,
      render: (value: any) => (
        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
          {value?.nombre || 'Sin sucursal'}
        </div>
      ),
    },
    {
      key: 'interno',
      label: 'Interno',
      sortable: true,
      render: (value: number) => (
        <div className="text-sm text-gray-900 dark:text-gray-100">{value || '-'}</div>
      ),
    },
    {
      key: 'nroSerie',
      label: 'NroSerie',
      sortable: true,
      render: (value: string) => (
        <div className="text-sm text-gray-900 dark:text-gray-100">{value || '-'}</div>
      ),
    },
    {
      key: 'tipoDeCarga',
      label: 'Carga',
      sortable: true,
      render: (value: any) => (
        <div className="text-sm text-gray-900 dark:text-gray-100">{value?.nombre || '-'}</div>
      ),
    },
    {
      key: 'capacidad',
      label: 'Capacidad',
      sortable: true,
      render: (value: any) => (
        <div className="text-sm text-gray-900 dark:text-gray-100">
          {value ? `${value.valor} ${value.unidad || ''}` : '-'}
        </div>
      ),
    },
    {
      key: 'vencimientoCarga',
      label: 'Venc. Carga',
      sortable: true,
      render: (value: string) => (
        <div className="text-sm text-gray-900 dark:text-gray-100">
          {new Date(value).toLocaleDateString('es-AR')}
        </div>
      ),
    },
    {
      key: 'vencimientoPH',
      label: 'Venc. PH',
      sortable: true,
      render: (value: string) => (
        <div className="text-sm text-gray-900 dark:text-gray-100">
          {new Date(value).toLocaleDateString('es-AR')}
        </div>
      ),
    },
    {
      key: 'fechaRecepcion',
      label: 'Fecha de recepcion del servidor',
      sortable: true,
      render: (value: string) => (
        <div className="text-sm text-gray-900 dark:text-gray-100">
          {value ? new Date(value).toLocaleDateString('es-AR') : '-'}
        </div>
      ),
    },
    {
      key: 'orden',
      label: 'Orden',
      sortable: true,
      render: (value: number) => (
        <div className="text-sm text-gray-900 dark:text-gray-100">{value || '-'}</div>
      ),
    },
    {
      key: 'año',
      label: 'Año',
      sortable: true,
      render: (value: number) => (
        <div className="text-sm text-gray-900 dark:text-gray-100">{value || '-'}</div>
      ),
    },
    {
      key: 'iram',
      label: 'IRAM',
      sortable: true,
      render: (value: string) => (
        <div className="text-sm text-gray-900 dark:text-gray-100">{value || '-'}</div>
      ),
    },
  ];

  const filterConfig: FilterConfig[] = [
    {
      key: 'cliente',
      label: 'Cliente',
      type: 'select',
      options: clienteOptions,
    },
    {
      key: 'sucursal',
      label: 'Sucursal',
      type: 'select',
      dependsOn: 'cliente',
      options: sucursalOptions,
    },
    {
      key: 'estado',
      label: 'Estado',
      type: 'select',
      options: EXTINTOR_ESTADOS.map((e) => ({ value: e.value, label: e.label })),
    },
  ];

  if (loading && extintores.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Extintores</h1>
          <p className="text-gray-600 dark:text-gray-400">
            Elegí un cliente para listar. Sucursal y búsqueda afinan el resultado.
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <ViewToggle currentView={viewMode} onViewChange={setViewMode} />
        </div>
      </div>

      {viewMode === 'table' ? (
        <DataTable
          key={centerId}
          data={extintores}
          columns={columns}
          title="Lista de Extintores"
          onAdd={handleAdd}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onView={handleView}
          searchPlaceholder="Buscar por código, serie, ubicación o sucursal..."
          itemsPerPage={LIST_PAGE_SIZE}
          loading={loading}
          emptyMessage={GATE_EMPTY}
          defaultShowFilters
          searchHint={GATE_SEARCH_HINT}
          filters={filterConfig}
          enableAdvancedFilters={true}
          onFilterChange={handleFilterChange}
          initialFilters={currentFilters}
          serverPagination={{
            page,
            pageSize: LIST_PAGE_SIZE,
            hasMore,
            totalCount,
            onPageChange: handlePageChange,
          }}
          actions={[
            {
              label: 'Ver',
              icon: EyeIcon,
              onClick: handleView,
              className: 'text-gray-400 hover:text-blue-500',
            },
            {
              label: 'Editar',
              icon: PencilIcon,
              onClick: handleEdit,
              className: 'text-gray-400 hover:text-yellow-500',
            },
            {
              label: 'Eliminar',
              icon: TrashIcon,
              onClick: handleDelete,
              className: 'text-gray-400 hover:text-red-500',
            },
          ]}
        />
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {extintores.map((extintor, _index) => (
              <div
                key={extintor.id}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-md transition-shadow duration-200 h-full flex flex-col"
              >
                <div className="p-6 flex flex-col flex-1 min-h-0">
                  {/* Header */}
                  <div className="mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white break-words">
                      {extintor.sucursal?.nombre || 'Sin sucursal'}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Interno: {extintor.interno || '-'} | Serie: {extintor.nroSerie || '-'}
                    </p>
                  </div>

                  {/* Details */}
                  <div className="space-y-3 flex-1">
                    {extintor.tipoDeCarga && (
                      <div className="text-sm">
                        <div className="text-gray-500 dark:text-gray-400 mb-1">Carga:</div>
                        <div className="text-gray-600 dark:text-gray-300">
                          {extintor.tipoDeCarga.nombre}
                        </div>
                      </div>
                    )}

                    {extintor.capacidad && (
                      <div className="text-sm">
                        <div className="text-gray-500 dark:text-gray-400 mb-1">Capacidad:</div>
                        <div className="text-gray-600 dark:text-gray-300">
                          {extintor.capacidad.valor} {extintor.capacidad.unidad}
                        </div>
                      </div>
                    )}

                    {extintor.vencimientoCarga && (
                      <div className="text-sm">
                        <div className="text-gray-500 dark:text-gray-400 mb-1">Venc. Carga:</div>
                        <div className="text-gray-600 dark:text-gray-300">
                          {new Date(extintor.vencimientoCarga).toLocaleDateString('es-AR')}
                        </div>
                      </div>
                    )}

                    {extintor.vencimientoPH && (
                      <div className="text-sm">
                        <div className="text-gray-500 dark:text-gray-400 mb-1">Venc. PH:</div>
                        <div className="text-gray-600 dark:text-gray-300">
                          {new Date(extintor.vencimientoPH).toLocaleDateString('es-AR')}
                        </div>
                      </div>
                    )}

                    {extintor.año && (
                      <div className="text-sm">
                        <div className="text-gray-500 dark:text-gray-400 mb-1">Año:</div>
                        <div className="text-gray-600 dark:text-gray-300">{extintor.año}</div>
                      </div>
                    )}

                    {extintor.iram && (
                      <div className="text-sm">
                        <div className="text-gray-500 dark:text-gray-400 mb-1">IRAM:</div>
                        <div className="text-gray-600 dark:text-gray-300">{extintor.iram}</div>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="mt-auto flex items-center justify-between h-12 shrink-0 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <div
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        extintor.activo
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                      }`}
                    >
                      {extintor.activo ? 'Activo' : 'Inactivo'}
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleView(extintor)}
                        className="p-2 text-gray-400 hover:text-blue-500 transition-colors duration-200"
                        title="Ver detalles"
                      >
                        <EyeIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleEdit(extintor)}
                        className="p-2 text-gray-400 hover:text-yellow-500 transition-colors duration-200"
                        title="Editar"
                      >
                        <PencilIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(extintor)}
                        className="p-2 text-gray-400 hover:text-red-500 transition-colors duration-200"
                        title="Eliminar"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <ServerPageBar
            page={page}
            pageSize={LIST_PAGE_SIZE}
            hasMore={hasMore}
            rowCount={extintores.length}
            totalCount={totalCount}
            loading={loading}
            onPageChange={handlePageChange}
          />
        </>
      )}

      {/* Notification */}
      {notification && (
        <Notification
          type={notification.type}
          message={notification.message}
          onClose={() => setNotification(null)}
        />
      )}
    </div>
  );
};

export default ExtintoresPage;
